import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, GraduationCap, Footprints, TrendingUp, Home, ArrowLeft, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { PropertyCard } from "../components/PropertyCard";

export function Neighborhoods() {
  const { t, L } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/neighborhoods").then((r) => setItems(r.data.items)); }, []);

  return (
    <div className="pt-24">
      <div className="border-b border-border bg-card/40">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
          <span className="label-caps text-primary">{t("home.neighborhoodsTag")}</span>
          <h1 className="font-serif text-4xl md:text-6xl font-light mt-3">{t("home.neighborhoodsTitle")}</h1>
          <p className="text-muted-foreground font-light mt-3 max-w-xl">{t("home.neighborhoodsSub")}</p>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((n) => (
          <Link key={n.id} to={`/neighborhoods/${n.slug}`} className="group relative aspect-[16/9] overflow-hidden hover-zoom" data-testid={`nb-card-${n.slug}`}>
            <img src={n.image} alt={n.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 scrim" />
            <div className="absolute bottom-0 p-8">
              <p className="label-caps text-primary mb-2">{n.city}</p>
              <h2 className="font-serif text-4xl text-white">{n.name}</h2>
              <div className="flex gap-5 mt-3 text-white/80 text-sm">
                <span>{n.stats?.avg_price} avg</span>
                <span>{n.stats?.listings} listings</span>
                <span>Walk {n.stats?.walk_score}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function NeighborhoodDetail() {
  const { slug } = useParams();
  const { t, L } = useLang();
  const [n, setN] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/neighborhoods/${slug}`).then((r) => setN(r.data)); }, [slug]);
  if (!n) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  const stats = [
    { icon: TrendingUp, l: "Avg Price", v: n.stats?.avg_price },
    { icon: Footprints, l: "Walk Score", v: n.stats?.walk_score },
    { icon: GraduationCap, l: "Schools", v: n.stats?.schools },
    { icon: Home, l: "Listings", v: n.stats?.listings },
  ];

  return (
    <div className="pt-24">
      <section className="relative h-[60vh] min-h-[420px]">
        <img src={n.image} alt={n.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute bottom-0 max-w-[1400px] mx-auto px-5 lg:px-10 w-full left-1/2 -translate-x-1/2 pb-10">
          <Link to="/neighborhoods" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-primary mb-4" data-testid="nb-back"><ArrowLeft size={16} /> {t("nav.neighborhoods")}</Link>
          <p className="label-caps text-primary">{n.city}</p>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-white">{n.name}</h1>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-12">
          {stats.map((s, i) => (
            <div key={i} className="bg-card p-6 text-center">
              <s.icon size={22} className="text-primary mx-auto mb-2" strokeWidth={1.3} />
              <p className="font-serif text-2xl">{s.v}</p>
              <p className="label-caps text-muted-foreground text-[0.6rem] mt-1">{s.l}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="font-serif text-3xl mb-4">{t("detail.overview")}</h2>
            <p className="text-muted-foreground font-light text-lg leading-relaxed">{L(n.description)}</p>
          </div>
          <div>
            <h3 className="label-caps text-primary mb-4">Highlights</h3>
            <div className="grid grid-cols-1 gap-2">
              {n.highlights?.map((h) => (
                <div key={h} className="flex items-center gap-3 py-3 border-b border-border font-light"><MapPin size={16} className="text-primary" /> {h}</div>
              ))}
            </div>
          </div>
        </div>

        {n.properties?.length > 0 && (
          <>
            <h2 className="font-serif text-4xl font-light mb-8">Available in {n.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {n.properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
